import { Box, Card, Divider } from '@mui/material';
import type { ReactNode } from 'react';

interface PanelCardProps {
  /** Header title — rendered at 13/600/text.primary per the prototype. */
  title?: ReactNode;
  /** Optional header right slot — link, toggle, gear, etc. */
  action?: ReactNode;
  /**
   * Body padding. `18` matches the prototype's `PanelCard`; pass `0` for
   * panels whose body owns its own padding (e.g. the MyWeekStrip whose
   * day-cells fill edge-to-edge).
   */
  pad?: number;
  /**
   * Header underline divider. Defaults to `true` (the Dashboard's carded look);
   * pass `false` for the prototype's `PdCard` style where the title sits
   * directly above the body with no rule (project-detail panes).
   */
  divider?: boolean;
  /**
   * Collapse the action slot to zero height so a taller action can't inflate
   * the header — keeps every Dashboard card's title/divider aligned. Defaults
   * to `true`. Pass `false` when the action is a full-height control (e.g. a
   * "Create allocation" button on a project-detail pane), so the header sizes
   * to it and the button keeps normal top padding instead of crowding the edge.
   */
  anchorAction?: boolean;
  children: ReactNode;
  /** Optional test id. */
  'data-testid'?: string;
}

/**
 * Canonical panel surface for the redesigned Dashboard. Mirrors the
 * prototype's `PanelCard` (the v2 prototype):
 * neutral surface, 13px / 600 title, header underline divider, 18px body
 * padding. Every panel widget (charts, lists, etc.) should compose this
 * shell — no ad-hoc `<Card>` blocks.
 */
export function PanelCard({
  title,
  action,
  pad = 18,
  divider = true,
  anchorAction = true,
  children,
  'data-testid': testId,
}: PanelCardProps) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }} data-testid={testId}>
      {(title || action) && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: '18px',
              py: '14px',
              minHeight: 48,
            }}
          >
            <Box
              component="h2"
              sx={{
                m: 0,
                fontSize: '0.8125rem', // 13px
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.4,
                minWidth: 0,
              }}
            >
              {title}
            </Box>
            {action ? (
              // Anchor the action at zero height, centred on the title's
              // mid-line, so a taller action (toggle group, "See all" link)
              // can't inflate the header. Every card's title + divider then
              // sit at the same position regardless of the action slot's
              // contents. `anchorAction={false}` opts out so a full-height
              // button sizes the header and keeps its normal top padding.
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  ...(anchorAction && { height: 0 }),
                }}
              >
                {action}
              </Box>
            ) : null}
          </Box>
          {divider && <Divider />}
        </>
      )}
      <Box sx={{ p: `${pad}px` }}>{children}</Box>
    </Card>
  );
}
